import React, { useMemo } from 'react';
import { TreeTaskCard } from 'shared/components/connected/task-card';
import {
  RenderItemProps,
  rootId,
  TreeItem,
  TreeItemTypes,
  VirtualizedTreeList,
} from 'shared/components/ui/virtualized-tree-list';
import { useToday } from 'shared/contexts/today';
import { ID } from 'shared/types/id';
import { Task } from 'shared/types/task';
import { TaskPriorityOptions } from 'shared/types/task-base';

import { usePriorityTree } from '../../hooks/use-priority-tree';
import { GroupedListProps } from '../../types';
import { PriorityHeader } from '../priority-header';

const taskTreeItemTypes = [TreeItemTypes.Task];

export const PriorityGroupingTasksList: React.FC<GroupedListProps> = ({
  tasks,
  sortingMode,
  onReorder,
  customOrder = {},
  collapsedSections = [],
  onSectionCollapse,
  weekStartsOn,
  scrollElement,
}) => {
  const today = useToday();

  const tasksMap = useMemo(
    () =>
      tasks.reduce<Record<ID, Task>>((acc, task) => {
        acc[task.id] = task;
        return acc;
      }, {}),
    [tasks],
  );

  const treeData = usePriorityTree({
    tasks,
    customOrder,
    sortingMode,
    today,
    weekStartsOn,
  });

  const _onReorder = (updatedItems: TreeItem[]) => {
    if (!onReorder) {
      return;
    }

    const updatedDocs: Task[] = [];
    const updatedSections: { id: ID; taskSorting: ID[] }[] = [];
    updatedItems.forEach((item) => {
      if (item.type === TreeItemTypes.Task) {
        if (!tasksMap[item.id]) {
          return;
        }
        const taskToUpdate = structuredClone(tasksMap[item.id]);
        const priorityItem = updatedItems.find(({ children }) =>
          children?.includes(item.id),
        );

        taskToUpdate.priority =
          (priorityItem?.id as TaskPriorityOptions) ?? TaskPriorityOptions.None;

        updatedDocs.push(taskToUpdate);
      }
      if (item.type === TreeItemTypes.Priority) {
        updatedSections.push({ id: item.id, taskSorting: item.children ?? [] });
      }
    });

    onReorder(updatedSections, updatedDocs);
  };

  const renderItem: React.FC<RenderItemProps> = ({
    item,
    itemInstance,
    tree,
  }) => {
    const type = item.type;

    // Task
    if (type === TreeItemTypes.Task) {
      return tasksMap[item.sourceId] ? (
        <TreeTaskCard
          task={tasksMap[item.sourceId]}
          tree={tree}
          item={itemInstance}
          tasks={tasks}
        />
      ) : null;
    }

    // Priority
    if (type === TreeItemTypes.Priority) {
      return <PriorityHeader item={itemInstance} />;
    }
  };

  return (
    <VirtualizedTreeList
      rootItemId={rootId}
      items={treeData}
      renderItem={renderItem}
      scrollElement={scrollElement}
      multiselectTypes={taskTreeItemTypes}
      onReorder={_onReorder}
      collapsedItems={collapsedSections}
      onCollapse={onSectionCollapse}
    />
  );
};
